<template>
  <v-row no-gutters class="fill-height flex-column pr-5 pb-5">
    <card-title-row class="pl-0 pt-5" translation-path="sidebar.waterBalanceZone.title" :has-buttons="false" />
    <water-balance-wizard
      ref="balance-wizard"
      with-profiles
      use-analysis-date
      tool-name="balance"
      :inputs="stepsInputs"
      :steps="steps"
      :params-proxy.sync="params"
      :steps-validations.sync="stepsValidations"
      :profiles-data="profilesData"
    >
      <template #stepPrepend-2>
        <v-col cols="auto" class="px-0">
          <base-button-subheader
            translation-path="sidebar.waterBalanceZone.equations"
            :disabled="
              ['dma_layer', 'dma_name', 'flow_meter_layer', 'flow_meter_name', 'dma_inflow', 'dma_outflow'].findIndex(
                paramName => !params[paramName]
              ) > -1
            "
            @click="openEquationsDialog"
          />
        </v-col>
        <balance-equations-dialog
          v-if="isEquationsDialogLoaded"
          :is-visible.sync="isEquationsDialogVisible"
          v-bind="equationsDialogBind"
        />
      </template>
    </water-balance-wizard>
  </v-row>
</template>

<script>
import WaterBalanceWizard from '@/components/WaterBalanceWizard';
import CardTitleRow from '@/components/CardTitleRow';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { get, call } from 'vuex-pathify';

export default {
  name: 'WaterBalanceZone',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    WaterBalanceWizard,
    CardTitleRow,
    BalanceEquationsDialog: () => import('@/components/BalanceEquationsDialog'),
  },
  data: () => ({
    isEquationsDialogLoaded: false,
    isEquationsDialogVisible: false,
    params: {},
    stepsValidations: {},
  }),
  methods: {
    getBalanceProfiles: call(`waterBalance/getBalanceProfiles`),
    saveBalanceProfile: call(`waterBalance/saveBalanceProfile`),
    editBalanceProfile: call(`waterBalance/editBalanceProfile`),
    deleteBalanceProfile: call(`waterBalance/deleteBalanceProfile`),
    openEquationsDialog() {
      this.isEquationsDialogLoaded = true;
      this.isEquationsDialogVisible = true;
    },
  },
  computed: {
    projectElements: get('layers/project@layers'),
    balanceProfiles: get('waterBalance/balanceProfiles'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    dmaLayers() {
      return this.projectLayers.filter(layer => ['polygon', 'multipolygon'].includes(layer.geometry_type));
    },
    watermetersLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_watermeters');
    },
    waterPointLayers() {
      return this.projectLayers.filter(
        layer => layer.layer_scope === 'water' && ['point', 'multipoint'].includes(layer.geometry_type)
      );
    },
    defaultInputProps() {
      return {
        dataType: { name: 'select' },
        rules: [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')],
        cols: '7',
        nameCols: '5',
      };
    },
    steps() {
      return [
        {
          step: 1,
          translationPath: 'sidebar.waterBalanceZone.flowmeters',
          backDisabled: true,
          nextDisabled: !this.stepsValidations['step1'],
        },
        {
          step: 2,
          translationPath: 'sidebar.waterBalanceZone.dma',
          backDisabled: this.$refs['balance-wizard']?.$data.isAnalysisLoading,
          nextDisabled: !this.stepsValidations['step2'] || this.$refs['balance-wizard']?.$data.isAnalysisLoading,
        },
        {
          step: 3,
          translationPath: 'sidebar.waterBalanceZone.watermeters',
          backDisabled: this.$refs['balance-wizard']?.$data.isAnalysisLoading,
          nextDisabled: !this.stepsValidations['step3'] || this.$refs['balance-wizard']?.$data.isAnalysisLoading,
        },
        {
          step: 4,
          translationPath: 'sidebar.waterBalanceZone.analysis',
          backDisabled: this.$refs['balance-wizard']?.$data.isAnalysisLoading,
          nextDisabled: false,
        },
      ];
    },
    stepsInputs() {
      return [
        {
          name: 'flow_meter_layer',
          isLayer: true,
          step: 1,
          translationPath: 'sidebar.waterBalanceZone.flowmetersLayer',
          items: this.waterPointLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.waterPointLayers?.length === 0,
          tooltip:
            this.waterPointLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceZone.addFlowmetersToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_scada',
          step: 1,
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceZone.scadaAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_name',
          step: 1,
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceZone.flowmetersNameAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_layer',
          isLayer: true,
          step: 2,
          translationPath: 'sidebar.waterBalanceZone.dmaLayer',
          items: this.dmaLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.dmaLayers?.length === 0,
          tooltip: this.dmaLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceZone.addDmaToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'dma_name',
          step: 2,
          connectedLayer: 'dma_layer',
          translationPath: 'sidebar.waterBalanceZone.dmaNameAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['dma_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_inflow',
          step: 2,
          connectedLayer: 'dma_layer',
          translationPath: 'sidebar.waterBalanceZone.dmaPositiveFlowAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          subtitlePath: 'sidebar.waterBalanceZone.dmaPositiveFlowSubtitle',
          hasSubtitle: true,
          hasSmallSubtitle: true,
          disabled: !this.params['dma_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_outflow',
          step: 2,
          connectedLayer: 'dma_layer',
          translationPath: 'sidebar.waterBalanceZone.dmaNegativeFlowAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          subtitlePath: 'sidebar.waterBalanceZone.dmaNegativeFlowSubtitle',
          hasSubtitle: true,
          hasSmallSubtitle: true,
          disabled: !this.params['dma_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_layer',
          isLayer: true,
          step: 3,
          translationPath: 'sidebar.waterBalanceZone.watermetersLayer',
          items: this.watermetersLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.watermetersLayers?.length === 0,
          tooltip:
            this.watermetersLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceZone.addWatermetersToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_daily_usage',
          step: 3,
          connectedLayer: 'watermeters_layer',
          translationPath: 'sidebar.waterBalanceZone.averageConsumptionAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeters_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_type',
          step: 3,
          connectedLayer: 'watermeters_layer',
          connectedValue: ['watermeters_domestic_values', 'watermeters_internal_values', 'watermeters_sold_values'],
          translationPath: 'sidebar.waterBalanceZone.typeAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeters_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_sold_values',
          step: 3,
          connectedAttribute: 'watermeters_type',
          translationPath: 'sidebar.waterBalanceZone.soldWaterValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_type'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_domestic_values',
          step: 3,
          connectedAttribute: 'watermeters_type',
          translationPath: 'sidebar.waterBalanceZone.houseTypeValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_type'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_internal_values',
          step: 3,
          connectedAttribute: 'watermeters_type',
          translationPath: 'sidebar.waterBalanceZone.ownPurposeValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_type'],
          ...this.defaultInputProps,
        },
      ];
    },
    equationsDialogBind() {
      return {
        dmaZonesLayerId: this.params['dma_layer'],
        dmaZonesLayerAttributeName: this.params['dma_name'],
        flowMeterLayerId: this.params['flow_meter_layer'],
        flowMeterLayerAttributeName: this.params['flow_meter_name'],
        dmaInflowAttribute: this.params['dma_inflow'],
        dmaOutflowAttribute: this.params['dma_outflow'],
      };
    },
    profilesData() {
      return {
        translationPaths: {
          addProfileDialogTitle: 'dialog.title.profiles.waterBalanceBalanceAdd',
          editProfileDialogTitle: 'dialog.title.profiles.waterBalanceBalanceEdit',
          deleteProfileDialogTitle: 'dialog.title.profiles.waterBalanceBalanceDelete',
        },
        profiles: this.balanceProfiles,
        getProfiles: this.getBalanceProfiles,
        saveProfile: this.saveBalanceProfile,
        editProfile: this.editBalanceProfile,
        deleteProfile: this.deleteBalanceProfile,
      };
    },
  },
};
</script>
